import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, RotateCcw } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const groups = [
  { key: "goal", label: "Seu objetivo", options: ["Ganhar massa", "Perder peso", "Energia e foco", "Saúde e longevidade"] },
  { key: "routine", label: "Sua rotina", options: ["Treino de manhã", "Treino à noite", "3x por semana", "Todos os dias"] },
  { key: "restriction", label: "Restrições", options: ["Nenhuma", "Vegetariano", "Sem lactose"] },
];

const renderMd = (text) =>
  text.split(/(\*\*[^*]+\*\*)/g).map((part, i) =>
    part.startsWith("**") ? (
      <strong key={i} className="text-baby font-semibold">{part.slice(2, -2)}</strong>
    ) : (
      <span key={i}>{part}</span>
    )
  );

export const AiRecommender = () => {
  const [sel, setSel] = useState({ goal: null, routine: null, restriction: null });
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);
  const ready = sel.goal && sel.routine && sel.restriction;

  const run = async () => {
    setLoading(true);
    setText("");
    try {
      const res = await fetch(`${API}/recommend`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(sel),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith("data:")) continue;
          const payload = line.slice(5).trim();
          if (payload === "[DONE]") break;
          try {
            const { t } = JSON.parse(payload);
            if (t) setText((prev) => prev + t);
          } catch {}
        }
      }
    } catch {
      setText("Não consegui me conectar agora. Tente novamente em instantes.");
    }
    setLoading(false);
  };

  return (
    <section id="consultora" data-testid="ai-recommender-section" className="bg-military-dark py-24 md:py-32 lg:py-40 relative overflow-hidden">
      <div className="absolute -bottom-40 -left-40 w-[36rem] h-[36rem] rounded-full bg-baby/10 blur-3xl pointer-events-none" />
      <div className="w-full max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24 grid grid-cols-1 lg:grid-cols-12 gap-16">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="lg:col-span-5"
        >
          <p className="text-xs uppercase tracking-[0.3em] text-baby font-semibold mb-4 flex items-center gap-2">
            <Sparkles size={14} /> 03 · Consultora IA
          </p>
          <h2 className="font-serif text-4xl md:text-6xl text-offwhite tracking-tight mb-6">
            Seu stack, <span className="italic font-light text-baby">sob medida.</span>
          </h2>
          <p className="text-offwhite/60 text-base md:text-lg leading-relaxed mb-12 max-w-md">
            Três respostas e nossa inteligência monta uma recomendação personalizada a partir do catálogo leve essenza.
          </p>
          {groups.map((g) => (
            <div key={g.key} className="mb-8">
              <p className="text-xs uppercase tracking-[0.25em] text-offwhite/50 font-semibold mb-3">{g.label}</p>
              <div className="flex flex-wrap gap-2" data-testid={`ai-group-${g.key}`}>
                {g.options.map((o) => (
                  <button
                    key={o}
                    data-testid={`ai-option-${g.key}-${o.toLowerCase().replace(/\s/g, "-")}`}
                    onClick={() => setSel((s) => ({ ...s, [g.key]: o }))}
                    className={`rounded-full px-5 py-2.5 text-xs uppercase tracking-widest font-semibold transition-colors duration-300 ${
                      sel[g.key] === o ? "bg-baby text-military-dark" : "border border-offwhite/20 text-offwhite/70 hover:border-baby/60 hover:text-offwhite"
                    }`}
                  >
                    {o}
                  </button>
                ))}
              </div>
            </div>
          ))}
          <button
            data-testid="ai-generate-button"
            onClick={run}
            disabled={!ready || loading}
            className="mt-4 rounded-full bg-baby text-military-dark px-8 py-4 text-sm uppercase tracking-widest font-semibold disabled:opacity-30 disabled:cursor-not-allowed hover:bg-offwhite transition-colors duration-300 flex items-center gap-2"
          >
            <Sparkles size={16} /> {loading ? "Analisando..." : "Gerar recomendação"}
          </button>
        </motion.div>

        <div className="lg:col-span-7 flex items-stretch">
          <AnimatePresence mode="wait">
            {text || loading ? (
              <motion.div
                key="result"
                data-testid="ai-result-panel"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.6, ease: "easeOut" }}
                className="w-full bg-offwhite/5 backdrop-blur-xl border border-offwhite/10 rounded-lg p-8 md:p-12 flex flex-col"
              >
                <p className="text-xs uppercase tracking-[0.25em] text-baby font-semibold mb-6">Recomendação personalizada</p>
                <div className="font-serif text-xl md:text-2xl text-offwhite leading-relaxed whitespace-pre-wrap flex-1">
                  {renderMd(text)}
                  {loading && <span className="inline-block w-2 h-6 bg-baby ml-1 animate-pulse align-middle" />}
                </div>
                {!loading && text && (
                  <div className="mt-10 flex flex-wrap gap-4">
                    <a
                      href="#catalogo"
                      data-testid="ai-see-catalog-button"
                      onClick={(e) => { e.preventDefault(); window.__lenis?.scrollTo("#catalogo", { offset: -60, duration: 1.4 }); }}
                      className="rounded-full bg-baby text-military-dark px-6 py-3 text-xs uppercase tracking-widest font-semibold hover:bg-offwhite transition-colors duration-300"
                    >
                      Ver no catálogo
                    </a>
                    <button
                      data-testid="ai-reset-button"
                      onClick={() => { setText(""); setSel({ goal: null, routine: null, restriction: null }); }}
                      className="rounded-full border border-offwhite/20 text-offwhite/70 px-6 py-3 text-xs uppercase tracking-widest font-semibold hover:border-baby/60 transition-colors duration-300 flex items-center gap-2"
                    >
                      <RotateCcw size={14} /> Refazer
                    </button>
                  </div>
                )}
              </motion.div>
            ) : (
              <motion.div
                key="placeholder"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="w-full border border-dashed border-offwhite/15 rounded-lg p-12 flex flex-col items-center justify-center text-center min-h-[28rem]"
              >
                <Sparkles size={32} className="text-baby/40 mb-6" />
                <p className="font-serif italic text-2xl md:text-3xl text-offwhite/40 max-w-sm">
                  Sua recomendação aparecerá aqui, escrita em tempo real.
                </p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
};
